import { useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  chatActionError,
  chatSelfUserId,
  isSelfChatMessage,
  type ChatAccount,
  type ChatMessage,
} from "@/lib/chatConnect";
import { emojiForReactionName, useSlackEmoji } from "@/lib/emoji";
import { imageLinksInText, renderSlackText, textIsOnlyImageLinks } from "@/lib/slackText";

type Reaction = NonNullable<ChatMessage["reactions"]>[number];

const GROUP_WINDOW_MS = 5 * 60 * 1000;

/** Hues chosen to stay legible as tinted bubbles on both Hub light and dark themes. */
const SENDER_HUES = [205, 160, 28, 280, 345, 95, 190, 50, 250, 10];

function senderKey(message: ChatMessage): string {
  return message.userId || message.author.trim().toLowerCase();
}

export function hueFor(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return SENDER_HUES[Math.abs(hash) % SENDER_HUES.length]!;
}

function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function dayLabel(at: number): string {
  const date = new Date(at);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

export function Avatar({
  name,
  url,
  hue,
  presence,
}: {
  name: string;
  url?: string;
  hue: number;
  presence?: ChatMessage["presence"];
}) {
  const [failed, setFailed] = useState(false);
  const face =
    url && !failed ? (
      <img
        className="chat-avatar"
        src={url}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    ) : (
      <span className="chat-avatar chat-avatar-initials" style={{ ["--chat-hue" as string]: hue }} aria-hidden>
        {initials(name)}
      </span>
    );
  if (!presence) return face;
  const label =
    presence === "online" ? "Online" : presence === "away" ? "Away" : presence === "dnd" ? "Do not disturb" : "Offline";
  return (
    <span className={`chat-avatar-wrap is-${presence}`}>
      {face}
      <span className="chat-presence" title={label} aria-label={label} />
    </span>
  );
}

type Row =
  | { type: "day"; key: string; label: string }
  | {
      type: "message";
      key: string;
      message: ChatMessage;
      self: boolean;
      hue: number;
      showHeader: boolean;
      lastInGroup: boolean;
    };

type Props = {
  messages: ChatMessage[];
  account: ChatAccount | undefined;
  /** Saves an edit to one of the connected account's messages; throw to keep the editor open. */
  onEdit?: (message: ChatMessage, text: string) => Promise<void>;
  /** Asks to delete one of the connected account's messages (the page confirms first). */
  onDelete?: (message: ChatMessage) => void;
  /** Opens the thread (in Hub, or in the provider when Hub can't show it). Only offered for real threads. */
  onOpenThread?: (message: ChatMessage) => void;
  /** True when threads open inside Hub; false labels the reply count "Open in Slack". */
  threadsInHub?: boolean;
  /** Present only when the account can really add/remove reactions. */
  onToggleReaction?: (message: ChatMessage, reaction: Reaction) => void;
  /** Press and hold, or right-click, opens the message menu. */
  onHold?: (message: ChatMessage) => void;
};

export function ChatMessageBubbles({
  messages,
  account,
  onEdit,
  onDelete,
  onOpenThread,
  threadsInHub,
  onToggleReaction,
  onHold,
}: Props) {
  useSlackEmoji();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const heldRef = useRef(false);

  function startEdit(message: ChatMessage) {
    setEditingId(message.id);
    setDraft(message.text);
    setEditError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError("");
  }

  async function saveEdit(message: ChatMessage, event?: FormEvent) {
    event?.preventDefault();
    const text = draft.trim();
    if (!onEdit || !text || saving) return;
    if (text === message.text.trim()) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await onEdit(message, text);
      setEditingId(null);
    } catch (err) {
      setEditError(chatActionError(err, "Could not edit the message."));
    } finally {
      setSaving(false);
    }
  }

  function onEditKeyDown(message: ChatMessage, event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEdit();
    } else if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void saveEdit(message);
    }
  }

  const selfUserId = chatSelfUserId(account);
  const userNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of messages) if (m.userId && m.author && m.author !== m.userId) map.set(m.userId, m.author);
    return map;
  }, [messages]);

  const rows = useMemo(() => {
    const out: Row[] = [];
    let lastDay = "";
    messages.forEach((message, i) => {
      if (message.at != null) {
        const day = new Date(message.at).toDateString();
        if (day !== lastDay) {
          out.push({ type: "day", key: `day-${day}`, label: dayLabel(message.at) });
          lastDay = day;
        }
      }
      const prev = messages[i - 1];
      const next = messages[i + 1];
      const key = senderKey(message);
      const sameAs = (other: ChatMessage | undefined) =>
        Boolean(
          other &&
            senderKey(other) === key &&
            message.at != null &&
            other.at != null &&
            Math.abs(message.at - other.at) < GROUP_WINDOW_MS &&
            new Date(other.at).toDateString() === new Date(message.at).toDateString(),
        );
      out.push({
        type: "message",
        key: message.id,
        message,
        self: isSelfChatMessage(message, account),
        hue: hueFor(key),
        showHeader: !sameAs(prev),
        lastInGroup: !sameAs(next),
      });
    });
    return out;
  }, [messages, account]);

  return (
    <ul className="chat-message-list">
      {rows.map((row) => {
        if (row.type === "day") {
          return (
            <li key={row.key} className="chat-day-divider" role="separator">
              <span>{row.label}</span>
            </li>
          );
        }
        const { message, self, hue, showHeader, lastInGroup } = row;
        const linkedImages = imageLinksInText(message.text);
        const images = [
          ...(message.images || []),
          ...linkedImages
            .filter((url) => !(message.images || []).some((img) => img.url === url))
            .map((url) => ({ url, alt: "" })),
        ];
        const showText = Boolean(message.text) && !(images.length && textIsOnlyImageLinks(message.text));
        const editing = editingId === message.id;
        const canDelete = self && Boolean(message.ts && onDelete);
        // chat.update with text only would drop image blocks, so GIF/image posts are delete-only.
        const canEdit = self && Boolean(message.ts && onEdit && message.text) && !images.length;
        return (
          <li
            key={row.key}
            className={`chat-message${self ? " is-self" : ""}${showHeader ? " group-start" : ""}${
              lastInGroup ? " group-end" : ""
            }`}
            style={{ ["--chat-hue" as string]: hue }}
          >
            <div className="chat-message-avatar-slot">
              {!self && showHeader ? (
                <Avatar name={message.author} url={message.avatarUrl} hue={hue} presence={message.presence} />
              ) : null}
            </div>
            <div className="chat-message-body">
              {showHeader ? (
                <div className="chat-message-meta">
                  {!self ? <strong>{message.author}</strong> : null}
                  {!self && message.statusText ? <span className="chat-status">{message.statusText}</span> : null}
                  {!self && message.timeZone ? <span className="chat-tz">{message.timeZone}</span> : null}
                  {message.at != null ? (
                    <time dateTime={new Date(message.at).toISOString()}>{formatTime(message.at)}</time>
                  ) : null}
                </div>
              ) : null}
              {editing ? (
                <form className="chat-message-edit" onSubmit={(event) => void saveEdit(message, event)}>
                  <textarea
                    aria-label="Edit message"
                    value={draft}
                    rows={Math.min(8, Math.max(2, draft.split("\n").length))}
                    autoFocus
                    disabled={saving}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => onEditKeyDown(message, event)}
                  />
                  {editError ? (
                    <p className="chat-message-error" role="alert">
                      {editError}
                    </p>
                  ) : null}
                  <div className="chat-message-edit-actions">
                    <button type="button" className="secondary-btn" disabled={saving} onClick={cancelEdit}>
                      Cancel
                    </button>
                    <button type="submit" className="primary-btn" disabled={saving || !draft.trim()}>
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="chat-bubble-row">
                  <div
                    className="chat-bubble"
                    title={!showHeader && message.at != null ? formatTime(message.at) : undefined}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      onHold?.(message);
                    }}
                    onPointerDown={(event) => {
                      if (event.button !== 0) return;
                      heldRef.current = false;
                      const timer = window.setTimeout(() => {
                        heldRef.current = true;
                        onHold?.(message);
                      }, 480);
                      const clear = () => window.clearTimeout(timer);
                      event.currentTarget.addEventListener("pointerup", clear, { once: true });
                      event.currentTarget.addEventListener("pointerleave", clear, { once: true });
                      event.currentTarget.addEventListener("pointercancel", clear, { once: true });
                    }}
                    onClick={() => {
                      heldRef.current = false;
                    }}
                  >
                    {showText ? (
                      <p className="chat-message-text">
                        {renderSlackText(message.text, { userNames, selfUserId })}
                        {message.edited ? <span className="chat-message-edited"> (edited)</span> : null}
                      </p>
                    ) : null}
                    {images.map((img) => (
                      <a
                        key={img.url}
                        href={img.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chat-message-image"
                      >
                        <img src={img.url} alt={img.alt || "Image"} loading="lazy" referrerPolicy="no-referrer" />
                      </a>
                    ))}
                    {message.files?.map((file) => (
                      <a
                        key={file.url}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="chat-file"
                      >
                        {file.name}
                      </a>
                    ))}
                  </div>
                  {message.reactions?.length ? (
                    <ul className="chat-reactions" aria-label="Reactions">
                      {message.reactions.map((reaction) => {
                        const glyph = (reaction.name && emojiForReactionName(reaction.name)) || reaction.emoji;
                        const label = reaction.name ? `:${reaction.name}:` : glyph;
                        const body = (
                          <>
                            <span aria-hidden>{glyph}</span>
                            <span className="chat-reaction-count">{reaction.count}</span>
                          </>
                        );
                        return (
                          <li key={reaction.name || reaction.emoji}>
                            {onToggleReaction && message.ts ? (
                              <button
                                type="button"
                                className={reaction.mine ? "chat-reaction mine" : "chat-reaction"}
                                title={label}
                                aria-pressed={Boolean(reaction.mine)}
                                aria-label={`${label} ${reaction.count}`}
                                onClick={() => onToggleReaction(message, reaction)}
                              >
                                {body}
                              </button>
                            ) : (
                              <span
                                className={reaction.mine ? "chat-reaction mine is-static" : "chat-reaction is-static"}
                                title={label}
                                aria-label={`${label} ${reaction.count}`}
                              >
                                {body}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                  {message.replyCount && message.threadTs && onOpenThread ? (
                    <button type="button" className="chat-thread-link" onClick={() => onOpenThread(message)}>
                      {message.replyCount} {message.replyCount === 1 ? "reply" : "replies"}
                      {threadsInHub ? "" : " · Open in Slack"}
                    </button>
                  ) : null}
                  {self && message.readBy?.length ? (
                    <p className="chat-read">Read by {message.readBy.map((person) => person.name).join(", ")}</p>
                  ) : null}
                  {canEdit || canDelete ? (
                    <div className="chat-message-actions">
                      {canEdit ? (
                        <button
                          type="button"
                          className="chat-message-action"
                          aria-label="Edit message"
                          title="Edit"
                          onClick={() => startEdit(message)}
                        >
                          <Pencil size={14} />
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          className="chat-message-action"
                          aria-label="Delete message"
                          title="Delete"
                          onClick={() => onDelete?.(message)}
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
